<template>
  <!--
    @closeModal and :on-close are both required and were both missing. BaseDialog's own close button
    only EMITS `closeModal` — it never touches `isModalOpen` — so without the listener the X did
    nothing at all. `:on-close` is the same story for the click-outside handler. Every other dialog
    in the app wires at least the first; this one wired neither.
  -->
  <BaseDialog
    centered
    contentClass="scraper-health-detail-dialog"
    ref="dialog"
    @closeModal="closeDialog"
    :on-close="closeDialog"
  >
    <!--
      The dialog opens with the same answer the table gave. Without this it stated only the three
      lane states and never the rollup, so clicking a row marked "Hors service" led to a panel that
      never used the words — leaving the reader to re-derive the verdict they had just clicked on.
    -->
    <template slot="title">
      <div v-if="source" class="scraper-health-detail__heading">
        <span class="scraper-health-detail__title">{{ source.sourceId }}</span>
        <BaseTag preset="soft" :tone="serviceTone">{{ serviceLabel }}</BaseTag>
        <span class="scraper-health-detail__evaluated" v-tooltip="absolute(source.evaluatedAt)">
          {{ $t('page.admin-scraper-health.detail.evaluated') }} {{ when(source.evaluatedAt) }}
        </span>
      </div>
    </template>

    <template slot="content">
      <div v-if="source" class="scraper-health-detail">
        <!-- One block per lane. Crawl and scrape can disagree, and that disagreement is the point:
             crawl healthy plus scrape broken means links keep arriving while tenders are saved as
             near-empty shells, so the app looks full and nothing is obviously wrong. -->
        <!--
          Healthy lanes recede rather than disappear. The contrast between them is the whole point —
          "crawl fine, scrape broken" is what tells you tenders are arriving as empty shells — so
          collapsing the OK ones would hide the diagnosis. They just stop competing for attention.
        -->
        <div
          v-for="lane in source.lanes"
          :key="lane.kind"
          class="scraper-health-detail__lane"
          :class="`scraper-health-detail__lane--${laneSeverity(lane)}`"
        >
          <div class="scraper-health-detail__lane-header">
            <span class="scraper-health-detail__lane-kind">
              {{ $t(`page.admin-scraper-health.columns.${lane.kind}`) }}
            </span>
            <BaseTag preset="soft" :tone="toneFor(lane.state)">
              {{ $t(`page.admin-scraper-health.states.${lane.state}`) }}
            </BaseTag>
          </div>

          <div class="scraper-health-detail__reason">{{ reasonText(lane.reason) }}</div>

          <!-- Every relative timestamp here carries the absolute date as a title: "3 d ago" is the
               right thing to read at a glance, and the wrong thing to paste into a ticket. -->
          <dl class="scraper-health-detail__facts">
            <template v-if="lane.problemSince">
              <dt>{{ $t('page.admin-scraper-health.detail.problem-since') }}</dt>
              <dd v-tooltip="absolute(lane.problemSince)">{{ when(lane.problemSince) }}</dd>
            </template>

            <dt>{{ $t('page.admin-scraper-health.detail.last-success') }}</dt>
            <dd v-tooltip="absolute(lane.lastSuccessAt)">{{ when(lane.lastSuccessAt) }}</dd>

            <!-- Crawl only. The pair last-run vs last-success is the signature of selector drift:
                 ran ten minutes ago, has found nothing in three days. -->
            <template v-if="lane.kind === 'crawl'">
              <dt>{{ $t('page.admin-scraper-health.detail.last-run') }}</dt>
              <dd v-tooltip="absolute(lane.lastRunAt)">{{ when(lane.lastRunAt) }}</dd>
            </template>

            <!--
              Same field, two different quantities, so it cannot carry one label. On the job lanes it
              is failed/attempted over the evaluation window; on the index lane it is the share of
              recent writes still missing from Solr. Calling that second one "failure rate" was
              simply wrong, and it is why the index lane appeared to sit at a permanent 0%.

              The window matters too: a lane can read 0% while carrying thousands of un-retried
              failures, because those failures are older than the window. Without saying so, "0%"
              next to "313 un-retried" reads as a contradiction.
            -->
            <template v-if="lane.failureRatio !== null && lane.failureRatio !== undefined">
              <dt v-tooltip="ratioTooltip(lane)">{{ ratioLabel(lane) }}</dt>
              <dd>{{ Math.round(lane.failureRatio * 100) }}%</dd>
            </template>

            <template v-if="lane.failedOpenCount">
              <dt>{{ $t('page.admin-scraper-health.detail.failed-backlog') }}</dt>
              <dd>
                {{ lane.failedOpenCount }}
                <span v-if="lane.failedOldestAt" v-tooltip="absolute(lane.failedOldestAt)"
                  >({{ when(lane.failedOldestAt) }})</span
                >
              </dd>
            </template>

            <template v-if="lane.kind === 'index'">
              <dt>{{ $t('page.admin-scraper-health.detail.solr-newest') }}</dt>
              <dd v-tooltip="absolute(lane.solrNewestAt)">{{ when(lane.solrNewestAt) }}</dd>
            </template>

            <dt>{{ $t('page.admin-scraper-health.detail.evaluated') }}</dt>
            <dd v-tooltip="absolute(lane.evaluatedAt)">{{ when(lane.evaluatedAt) }}</dd>
          </dl>

          <!--
            Two levels of restraint here.

            Shown at all only when the error belongs to the problem being looked at. `last_error` is
            the last error EVER seen on the lane, so a healthy lane — or one whose current trouble
            started after that error — was displaying a months-old stack trace in the same
            red-labelled block as a live one. biddingo's crawl is the case in point: its state is
            "overdue" and the error under it is 370 days old. Historical errors collapse to their
            label and stay one click away rather than disappearing, because they are still the best
            clue when a source breaks the same way twice.

            Then, when shown, collapsed to the first line. A real error here is a 40-line Selenium
            capability dump and the actionable part is almost always line one ("no such element:
            #GridView1", "null value in column title violates not-null constraint").
          -->
          <div
            v-if="lane.lastError"
            class="scraper-health-detail__error"
            :class="{ 'scraper-health-detail__error--historical': errorDisplay(lane) === 'hidden' }"
          >
            <div class="scraper-health-detail__error-label">
              {{ $t('page.admin-scraper-health.detail.last-error') }}
              <span v-if="lane.lastErrorAt" v-tooltip="absolute(lane.lastErrorAt)">— {{ when(lane.lastErrorAt) }}</span>
              <button
                v-if="errorToggleLabel(lane)"
                type="button"
                class="scraper-health-detail__error-toggle"
                @click="toggleError(lane.kind)"
              >
                {{ errorToggleLabel(lane) }}
              </button>
            </div>
            <pre v-if="errorDisplay(lane) === 'full'">{{ lane.lastError }}</pre>
            <pre v-else-if="errorDisplay(lane) === 'first'">{{ firstLine(lane.lastError) }}</pre>
          </div>
        </div>

        <div class="scraper-health-detail__footer">
          <div v-if="source.paused" class="scraper-health-detail__paused-note">
            {{ $t('page.admin-scraper-health.detail.paused-by', { who: pausedBy, reason: pausedReason }) }}
          </div>

          <!--
            The pause form stays behind a deliberate click. This dialog is opened to read a
            diagnosis — that is what a row click means — and pausing is the one irreversible-ish
            thing on the page: it silences every alert for a source until somebody remembers to
            un-pause it. Presenting an empty reason field to everyone who opens a row put the most
            consequential control on the screen by default, next to the least consequential one.

            A reason is still mandatory: a pause with nobody accountable and no stated reason is how
            a source stays quietly broken for a year.
          -->
          <div v-if="!source.paused && showPauseForm" class="scraper-health-detail__pause-form">
            <FormInput
              ref="pauseReasonInput"
              v-model="pauseReason"
              name="pause-reason"
              :label="$t('page.admin-scraper-health.actions.pause-reason-label')"
              :placeholder="$t('page.admin-scraper-health.actions.pause-reason-placeholder')"
              is-required
            />
          </div>

          <!--
            BaseButton with is-square, matching the admin surfaces rather than the rounded form
            buttons: this is a toolbar of actions on a record, not a form submit. Pause is is-danger
            because it silences alerting for a whole source, which is the one thing here that can
            hurt you quietly.
          -->
          <div class="scraper-health-detail__actions">
            <BaseButton is-square is-transparent @click.native="closeDialog">
              {{ $t('actions.close') }}
            </BaseButton>

            <BaseButton v-if="source.paused" is-square :is-loading="pending" @click.native="resume">
              {{ $t('page.admin-scraper-health.actions.resume') }}
            </BaseButton>

            <!-- Step one: reveal the form. Step two: confirm. -->
            <BaseButton v-else-if="!showPauseForm" is-square is-outlined @click.native="openPauseForm">
              {{ $t('page.admin-scraper-health.actions.pause') }}
            </BaseButton>

            <template v-else>
              <BaseButton is-square is-transparent @click.native="cancelPause">
                {{ $t('actions.cancel') }}
              </BaseButton>
              <BaseButton is-square is-danger :disabled="!canPause" :is-loading="pending" @click.native="pause">
                {{ $t('page.admin-scraper-health.actions.confirm-pause') }}
              </BaseButton>
            </template>
          </div>
        </div>
      </div>
    </template>
  </BaseDialog>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseDialog from '@/components/base/BaseDialog';
import BaseTag from '@/components/base/BaseTag.vue';
import FormInput from '@/components/form/FormInput';
import { scraperHealthService } from '@/services/scraper-health.service';
import { getScraperHealthServiceTone, getScraperHealthStateTone } from '@/util/tag-tones/scraper-health-state-tag';
import { formatAbsolute, formatRelativeToNow, getSourceServiceLevel } from '@/util/scraper-health-util';

export default {
  name: 'ScraperHealthDetailDialog',
  components: { BaseButton, BaseDialog, BaseTag, FormInput },
  data() {
    return {
      source: null,
      pauseReason: '',
      showPauseForm: false,
      pending: false,
      expandedErrors: {},
    };
  },
  computed: {
    canPause() {
      return this.pauseReason.trim().length > 0;
    },
    /** Pause provenance is written to every lane of the source, so any lane carries it. */
    pausedBy() {
      const lane = (this.source.lanes || []).find((l) => l.pausedBy);
      return lane ? lane.pausedBy : '';
    },
    pausedReason() {
      const lane = (this.source.lanes || []).find((l) => l.pausedReason);
      return lane ? lane.pausedReason : '';
    },
    serviceTone() {
      return getScraperHealthServiceTone(getSourceServiceLevel(this.source));
    },
    serviceLabel() {
      return this.$t(`page.admin-scraper-health.service.${getSourceServiceLevel(this.source)}`);
    },
  },
  methods: {
    open(source) {
      this.source = source;
      this.expandedErrors = {};
      this.pauseReason = '';
      // Reset every time: a dialog that reopens mid-pause, on a different source, with the previous
      // source's reason still typed in is how the wrong thing gets paused for the wrong stated cause.
      this.showPauseForm = false;
      this.$refs.dialog.openModal();
    },
    openPauseForm() {
      this.showPauseForm = true;
      // Focus the reason, so the click that revealed the form also puts the cursor where the only
      // required input is.
      this.$nextTick(() => {
        const input = this.$refs.pauseReasonInput;
        const el = input && input.$el ? input.$el.querySelector('input, textarea') : null;
        if (el) el.focus();
      });
    },
    cancelPause() {
      this.showPauseForm = false;
      this.pauseReason = '';
    },
    closeDialog() {
      this.$refs.dialog.isModalOpen = false;
    },
    /**
     * Whether the error is worth collapsing. Single-line errors — "No value present", "Missing
     * mandatory field(s)" — are already readable and a toggle on them is just noise.
     */
    isLongError(text) {
      return String(text || '').indexOf('\n') !== -1;
    },
    /**
     * Whether this error explains the state the lane is in now.
     *
     * `last_error` is the newest error ever recorded on the lane, with no link to the current
     * problem, so it needs a reference point to be judged against. That point is NOT simply
     * `problemSince`: a lane failing on backlog age is failing precisely because of old failures, and
     * its explanatory error is necessarily older than the episode. So compare against the oldest
     * failure still driving the state where there is one, and fall back to `problemSince` otherwise.
     *
     * biddingo shows both cases at once — its crawl error is 370 days old against an 11-hour-old
     * overdue state (history), while its scrape error sits inside the backlog window that is
     * actually causing the failure (diagnosis).
     */
    isErrorCurrent(lane) {
      if (lane.state === 'ok' || lane.state === 'paused') return false;
      if (!lane.lastErrorAt) return true; // cannot tell — showing it is the safer default

      const reference = lane.failedOldestAt || lane.problemSince;
      if (!reference) return true;
      return new Date(lane.lastErrorAt).getTime() >= new Date(reference).getTime();
    },
    /** 'full' once the user asks for it, otherwise 'first' for a current error and 'hidden' for history. */
    errorDisplay(lane) {
      if (this.expandedErrors[lane.kind]) return 'full';
      return this.isErrorCurrent(lane) ? 'first' : 'hidden';
    },
    /** Null when there is nothing more to reveal, which hides the toggle entirely. */
    errorToggleLabel(lane) {
      const display = this.errorDisplay(lane);
      if (display === 'full') return this.$t('page.admin-scraper-health.detail.error-collapse');
      if (display === 'hidden') return this.$t('page.admin-scraper-health.detail.error-show');
      return this.isLongError(lane.lastError)
        ? this.$t('page.admin-scraper-health.detail.error-expand', { lines: this.errorLines(lane.lastError) })
        : null;
    },
    /** Label and tooltip for the ratio row, which carries a different quantity per lane. */
    ratioLabel(lane) {
      return lane.kind === 'index'
        ? this.$t('page.admin-scraper-health.detail.solr-drift')
        : this.$t('page.admin-scraper-health.detail.failure-ratio');
    },
    ratioTooltip(lane) {
      return lane.kind === 'index'
        ? this.$t('page.admin-scraper-health.detail.solr-drift-help')
        : this.$t('page.admin-scraper-health.detail.failure-ratio-help');
    },
    errorLines(text) {
      return String(text || '').split('\n').length;
    },
    firstLine(text) {
      return String(text || '').split('\n')[0];
    },
    /** Per lane, so expanding the crawl error does not also expand scrape's unrelated one. */
    toggleError(kind) {
      this.$set(this.expandedErrors, kind, !this.expandedErrors[kind]);
    },
    toneFor(state) {
      return getScraperHealthStateTone(state);
    },
    /**
     * Drives the lane block's left edge and whether it recedes.
     *
     * Returns the state itself, not an ok/problem collapse: collapsing paints a `stale` lane's edge red
     * beside its own yellow tag — two signals for one state, disagreeing, and the louder one wrong.
     */
    laneSeverity(lane) {
      switch (lane.state) {
        case 'ok':
        case 'paused':
        case 'stale':
        case 'failing':
        case 'unknown':
          return lane.state;
        default:
          // An unrecognised state means the back-end added one. Paint it as the loudest problem
          // rather than letting it fall through to a transparent edge and read as healthy.
          return 'failing';
      }
    },
    when(value) {
      return value ? formatRelativeToNow(value, this.$i18n.locale) : '—';
    },
    /** The absolute date behind a relative one, for the title attribute. */
    absolute(value) {
      return value ? formatAbsolute(value) : '';
    },
    /**
     * Reason codes come from the evaluator (crawl_chain_broken, no_discovery, ...). Fall back to the
     * raw code rather than showing a missing-key placeholder: an unexplained but visible code is
     * still actionable, and a new check must not blank out the field.
     */
    reasonText(reason) {
      if (!reason) return '';
      const key = `page.admin-scraper-health.reasons.${reason}`;
      return this.$te(key) ? this.$t(key) : reason;
    },
    pause() {
      this.pending = true;
      scraperHealthService
        .pauseSource({ sourceId: this.source.sourceId, reason: this.pauseReason.trim() })
        .then(() => {
          this.$toast.success(this.$t('page.admin-scraper-health.actions.pause-succeeded'));
          this.$emit('action-succeeded');
          this.closeDialog();
        })
        .catch(() => this.$toast.error(this.$t('page.admin-scraper-health.actions.pause-failed')))
        .finally(() => {
          this.pending = false;
        });
    },
    resume() {
      this.pending = true;
      scraperHealthService
        .resumeSource(this.source.sourceId)
        .then(() => {
          this.$toast.success(this.$t('page.admin-scraper-health.actions.resume-succeeded'));
          this.$emit('action-succeeded');
          this.closeDialog();
        })
        .catch(() => this.$toast.error(this.$t('page.admin-scraper-health.actions.resume-failed')))
        .finally(() => {
          this.pending = false;
        });
    },
  },
};
</script>

<style lang="scss">
.base-dialog__scraper-health-detail-dialog {
  height: auto;
  width: 720px;
  max-width: 92vw;
  position: relative;

  .base-dialog__content {
    padding: 24px 32px 32px;
    background: $color-white !important;
  }
}

.scraper-health-detail {
  &__heading {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  &__title {
    font-family: monospace;
  }

  &__evaluated {
    font-size: 12px;
    font-weight: 400;
    color: $color-text-muted;
    margin-left: auto;
  }

  // A status edge rather than a full-width divider between equals. Three lanes rendered identically
  // meant a healthy one competed with a broken one for attention, and the eye had to read every tag
  // to find out which block mattered.
  // Separator on the TOP edge, not the bottom. With border-bottom the last lane drew a rule and the
  // sticky footer drew its own directly beneath it — two lines, 1px apart, above the buttons.
  // Dropping the first lane's top border gives the same separators with none left dangling.
  &__lane {
    padding: 14px 0 14px 14px;
    border-top: 1px solid $color-border;
    border-left: 3px solid transparent;

    &:first-child {
      border-top: none;
    }

    // One colour per state, mirroring getScraperHealthStateTone so the edge and the tag on the same row
    // never disagree. If that mapping changes, change these with it.
    &--failing {
      border-left-color: $color-semantic-error;
    }

    &--stale {
      border-left-color: $color-semantic-warning;
    }

    // Distinct from `stale`, not folded into it: "we could not measure this" is a different problem
    // from "we measured it and it is behind", and the runbook sends you somewhere else for each.
    &--unknown {
      border-left-color: $color-orange-500;
    }

    &--ok {
      border-left-color: $color-semantic-success-light;
      // Recedes, but stays legible: the crawl-ok / scrape-broken contrast IS the diagnosis, so a
      // healthy lane must remain readable rather than be collapsed away.
      opacity: 0.72;
    }

    &--paused {
      border-left-color: $color-border;
      opacity: 0.72;
    }
  }

  &__lane-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 6px;
  }

  &__lane-kind {
    font-weight: 600;
  }

  // The diagnosis, not a caption. This is the one sentence in the dialog that says what is wrong in
  // words, and it was styled like fine print — smaller and greyer than the raw timestamps below it.
  &__reason {
    font-size: 13.5px;
    color: $color-normal;
    margin-bottom: 12px;
  }

  &__facts {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 4px 16px;
    margin: 0;
    font-size: 13px;

    dt {
      color: $color-text-muted;
    }

    // justify-self, not the default stretch. A grid item stretches to fill its track, so each `dd`
    // was a 527px-wide box holding ~110px of text — and v-tooltip anchors to the box, so every date
    // tooltip appeared centred on the column, drifting ~150px right of the value it described.
    // Shrinking the item to its content makes the anchor and the visible text the same thing.
    dd {
      margin: 0;
      justify-self: start;
    }
  }

  &__error {
    margin-top: 12px;

    // A historical error is reference material, not a finding: it recedes to a single quiet line.
    &--historical {
      opacity: 0.6;
    }

    &-label {
      font-size: 12px;
      color: $color-text-muted;
      margin-bottom: 4px;
    }

    &-toggle {
      background: none;
      border: none;
      padding: 0 0 0 6px;
      font-size: 12px;
      color: $color-semantic-error;
      cursor: pointer;
      text-decoration: underline;
    }

    pre {
      margin: 0;
      padding: 10px;
      background: $color-grey-pale;
      border-radius: 6px;
      font-size: 12px;
      white-space: pre-wrap;
      word-break: break-word;
      max-height: 160px;
      overflow-y: auto;
    }
  }

  // Sticks to the bottom of the scrolling content so the actions are reachable without scrolling to
  // the end of three lane blocks, and separated by a rule so they read as controls rather than as
  // more content.
  &__footer {
    position: sticky;
    bottom: 0;
    padding-top: 16px;
    margin-top: 4px;
    background: $color-white;
    border-top: 1px solid $color-border;
  }

  &__paused-note {
    font-size: 13px;
    color: $color-text-muted;
    margin-bottom: 16px;
  }

  &__pause-form {
    margin-top: 4px;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 16px;
  }
}
</style>
